import { get } from "svelte/store";
import type { ConversionType, EntryPoint, Suite } from "#gen/bundles/v1/bundle_pb.js";
import type { ConversionMetaData } from "#gen/bundles/v1/events_pb.js";
import { Mode, RequestIntent } from "#gen/bundles/v1/service_pb.js";
import type { Code } from "#gen/codes/v3/codes_pb.js";
import type { ShortUrl } from "#gen/shorturls/v1/shorturls_pb.js";
import type { AssetLoad, BundleClient } from "#lib/api/bundles.js";
import { getTrackingCookieName } from "#lib/cookie.js";
import { Logger } from "#lib/logging.js";
import type { JsonObject } from "@bufbuild/protobuf";
import type { GeolocationCoords } from "svelte-geolocation/Geolocation.svelte";

export interface EntryAsset {
	$typeName: Code["$typeName"] | ShortUrl["$typeName"];
	id: string;
	name: string;
	state: string;
	metadataSchema?: string;
	metadata?: JsonObject;
}

export class AssetManager {
	private client: BundleClient;
	private assets: Map<string, Promise<AssetLoad>> = new Map();
	private cookie: string | undefined;

	constructor(client: BundleClient, cookie?: string) {
		this.client = client;
		this.cookie = cookie;
	}

	isLoaded(key: string): boolean {
		return this.assets.has(key);
	}

	async preload(suite: Suite, entryPoint: EntryPoint, keys: string[], mode: Mode, coords?: GeolocationCoords, mlMetadata?: ConversionMetaData, intent: RequestIntent = RequestIntent.DISPLAY): Promise<AssetLoad[]> {
		if (keys.length === 0) {
			return [];
		}
		const headers = new Headers();
		if (this.cookie) {
			headers.set(`x-${getTrackingCookieName()}`, this.cookie);
		}
		return await this.client.preload(suite, entryPoint, keys, coords, mlMetadata, mode, intent, { headers });
	}

	async load(suite: Suite, entryPoint: EntryPoint, key: string, mode: Mode, coords?: GeolocationCoords, mlMetadata?: ConversionMetaData): Promise<AssetLoad> {
		const headers = new Headers();
		if (this.cookie) {
			headers.set(`x-${getTrackingCookieName()}`, this.cookie);
		}
		return await this.client.load(suite, entryPoint, key, coords, mlMetadata, mode, { headers });
	}

	async execute(suite: Suite, entryPoint: EntryPoint, key: string, conversionType: ConversionType, conversionComponent: string, sourceAnchor: string | undefined, coords?: GeolocationCoords, data?: JsonObject, metadata?: ConversionMetaData, mode: Mode = Mode.LIVE, intent: RequestIntent = RequestIntent.DISPLAY): Promise<AssetLoad | string | undefined> {
		const headers = new Headers();
		if (this.cookie) {
			headers.set(`x-${getTrackingCookieName()}`, this.cookie);
		}
		return await this.client.execute(suite, entryPoint, key, conversionType, conversionComponent, sourceAnchor, coords, data, metadata, mode, intent, { headers });
	}

	async edit(suite: Suite, entryPoint: EntryPoint, key: string): Promise<AssetLoad> {
		if (this.isLoaded(key)) {
			return this.assets.get(key)!;
		}
		const promise = this.client.edit(suite, entryPoint, key);
		promise.catch(err => {
			Logger.error(`failed to edit asset ${key}`, { err });
			this.invalidate(key);
		});
		this.assets.set(key, promise);
		return promise;
	}

	async editPreload(suite: Suite, entryPoint: EntryPoint, keys: string[]): Promise<AssetLoad[]> {
		if (keys.length === 0) {
			return [];
		}
		const filtered = [];
		const result: AssetLoad[] = [];
		for (const key of keys) {
			if (this.isLoaded(key)) {
				result.push(await this.assets.get(key)!);
			} else {
				filtered.push(key);
			}
		}
		if (filtered.length === 0) {
			return result;
		}
		try {
			const assets = await this.client.editPreload(suite, entryPoint, filtered);
			for (const [key, asset] of Object.entries(assets)) {
				const promise = Promise.resolve(asset);
				this.assets.set(key, promise);
				result.push(asset);
			}
		} catch (err) {
			Logger.error(`failed to edit preload assets ${filtered.join(", ")}`, { err });
			this.invalidate(...filtered);
		}
		return result;
	}

	async save(suite: Suite, asset: AssetLoad, data: JsonObject): Promise<AssetLoad>;
	async save(suite: Suite, key: string, data: JsonObject): Promise<AssetLoad>;
	async save(suite: Suite, keyOrAsset: string | AssetLoad, data: JsonObject): Promise<AssetLoad> {
		if (typeof keyOrAsset !== "string") {
			const asset = keyOrAsset;
			const promise = this.client.save(suite, asset.key(), get(asset.data()) ?? {}, asset);
			this.assets.set(asset.key(), promise);
			promise.catch((err: unknown) => {
				this.invalidate(asset.key());
				Logger.error(`failed to save asset ${asset.key()}`, { err });
			});
			return promise;
		}
		const key = keyOrAsset;
		const promise = this.client.save(suite, key, data);
		this.assets.set(key, promise);
		promise.catch(err => {
			this.invalidate(key);
			Logger.error(`failed to save asset ${key}`, { err });
		});
		return promise;
	}

	async cancel(suite: Suite, entryPoint: EntryPoint, key: string, coords?: GeolocationCoords, metadata?: ConversionMetaData, mode: Mode = Mode.LIVE): Promise<void> {
		return this.client.cancel(suite, entryPoint, key, coords, metadata, mode);
	}

	invalidateAll() {
		this.assets.clear();
	}

	invalidate(...keys: string[]) {
		for (const key of keys) {
			this.assets.delete(key);
			this.assets.keys().forEach(childKey => {
				if (childKey.startsWith(`${key}:`)) {
					this.assets.delete(childKey);
				}
			});
		}
	}
}
